import useCategoryHook from "@/shared/hooks/useCategoryHook";
import useCategoryQueryHook from "@/shared/hooks/useCategoryQueryHook";
import useLayoutData from "@/shared/hooks/useLayoutData";
import { toLanguageFields } from "@/shared/mutations/menu/menuTranslationHelpers";
import useMenuDescriptionsPost from "@/shared/mutations/menu/useMenuDescriptionsPost";
import { getFieldByLang } from "@/shared/utils/helpers";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../Button/Button";
import Error from "../Error";
import Icon from "../Icon/Icon";
import Modal, { ModalProps } from "../Modal/Modal";
import ModalTitle from "../Modal/ModalTitle";
import Spinner from "../Spinner/Spinner";

// Writes a description for every product without one. Nothing is saved by the API,
// the successful ones are saved here with the menu, the failed ones can be tried again
const MenuDescriptionsModal = ({ onExit }: ModalProps) => {
  const { t } = useTranslation("", { keyPrefix: "menu.descriptionsModal" });
  const { t: tG } = useTranslation();
  const generate = useMenuDescriptionsPost();
  const { menuUpdate, refetch } = useCategoryHook();
  const { products } = useCategoryQueryHook();
  const { franchiseLanguages } = useLayoutData();
  const [isSaved, setIsSaved] = useState(false);

  // Started once on open, a second run would write the same descriptions again
  const hasStarted = useRef(false);
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    generate.mutate();
  }, []);

  const result = generate.data;
  const descriptions = result?.descriptions ?? [];
  const failedIds = result?.failed_item_ids ?? [];
  const fieldKeys = franchiseLanguages.map((language) => language.key);

  const getProductName = (itemId: number) => {
    const product = products.find((p) => p.item_id === itemId);
    return getFieldByLang(product?.name) || `#${itemId}`;
  };

  const handleRetry = () => {
    setIsSaved(false);
    generate.mutate();
  };

  // The name is sent along so the menu update keeps it
  const handleSave = () => {
    const content = descriptions.map((item) => ({
      item_id: item.item_id,
      name: products.find((p) => p.item_id === item.item_id)?.name,
      description: toLanguageFields(item.description, fieldKeys),
    }));
    menuUpdate.mutate(content, {
      onSuccess: () => {
        setIsSaved(true);
        refetch();
        if (failedIds.length === 0) onExit?.();
      },
    });
  };

  return (
    <Modal show onExit={onExit} closeOnClickOutside={false}>
      <ModalTitle>{t("title")}</ModalTitle>

      {generate.isPending && (
        <div className="flex flex-col items-center gap-4 py-12 text-text-secondary">
          <Spinner />
          <span>{t("pending")}</span>
        </div>
      )}

      {generate.isError && <Error refetch={handleRetry} />}

      {generate.isSuccess && result && (
        <div className="flex flex-col gap-6">
          {result.products === 0 ? (
            <p className="text-text-secondary">{t("noProducts")}</p>
          ) : (
            <p className="font-semibold">
              {t("summary", {
                success: descriptions.length,
                total: result.products,
              })}
            </p>
          )}

          {descriptions.length > 0 && (
            <div>
              <h3 className="mb-3 font-semibold text-success">
                {t("succeeded", { count: descriptions.length })}
              </h3>
              <ul className="flex max-h-[40vh] flex-col gap-3 overflow-y-auto">
                {descriptions.map((item) => (
                  <li key={item.item_id} className="flex gap-3">
                    <Icon
                      className="mt-1 size-6 shrink-0 fill-success"
                      variants="check"
                    />
                    <div>
                      <div>
                        {item.name}
                        <span className="text-small opacity-60">
                          {" "}
                          · {item.category}
                        </span>
                      </div>
                      <div className="text-small text-text-secondary">
                        {item.description.nl}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {failedIds.length > 0 && (
            <div>
              <h3 className="mb-1 font-semibold text-error">
                {t("failed", { count: failedIds.length })}
              </h3>
              <p className="mb-3 text-small text-text-secondary">
                {t("failedHint")}
              </p>
              <ul className="flex max-h-[20vh] flex-col gap-2 overflow-y-auto">
                {failedIds.map((itemId) => (
                  <li key={itemId} className="flex items-center gap-3">
                    <Icon
                      className="size-6 shrink-0 fill-error"
                      variants="times"
                    />
                    {getProductName(itemId)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isSaved && <p className="text-success">{t("saved")}</p>}

          <div className="flex gap-4">
            {!isSaved && descriptions.length > 0 && (
              <Button isPending={menuUpdate.isPending} onClick={handleSave}>
                {t("save")}
              </Button>
            )}
            {/* Unsaved descriptions would be written again, so retrying comes after saving */}
            {failedIds.length > 0 && (isSaved || descriptions.length === 0) && (
              <Button onClick={handleRetry}>{t("retry")}</Button>
            )}
            <Button variant="secondary" onClick={onExit}>
              {isSaved ? tG("close") : tG("cancel")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MenuDescriptionsModal;
